import { fetchBaseQuery, FetchBaseQueryError } from "@reduxjs/toolkit/query/react";
import type { BaseQueryFn } from "@reduxjs/toolkit/query";
import { InteractionRequiredAuthError, type AccountInfo } from "@azure/msal-browser";
import { IRootState } from "./app/store";
import { msalInstance } from "./main";
import { loginRequest } from "./authConfig";
import { getApiBaseUrl } from "./utils";
import { DISABLE_AUTH } from "./flags";
import { clearAccessToken, setAccessToken } from "./globalSlice";

const rawBaseQuery = fetchBaseQuery({
  baseUrl: `${getApiBaseUrl()}/api`,
  prepareHeaders: (headers, { getState }) => {
    const { accessToken } = (getState() as IRootState).global;
    if (accessToken) headers.set("Authorization", `Bearer ${accessToken}`);
    headers.set("Accept", "application/json");
    return headers;
  },
});

async function tryRefreshToken(): Promise<string | null> {
  const account = (msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0]) as
    | AccountInfo
    | undefined;
  if (!account) return null;

  try {
    const res = await msalInstance.acquireTokenSilent({ ...loginRequest, account });
    return res.accessToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      // kräver redirect → sparkar igång login
      msalInstance.loginRedirect(loginRequest);
    }
    return null;
  }
}

export const baseQueryWithReauth: BaseQueryFn = async (args, api, extraOptions) => {
  let result = await rawBaseQuery(args, api, extraOptions);
  if (DISABLE_AUTH) return result; // ⬅️ inga reauthförsök i dev
  const status = (result.error as FetchBaseQueryError | undefined)?.status;
  if (status === 401 || status === 403) {
    const newToken = await tryRefreshToken();
    if (newToken) {
      api.dispatch(setAccessToken({ accessToken: newToken }));
      // försök igen
      result = await rawBaseQuery(args, api, extraOptions);
    } else {
      api.dispatch(clearAccessToken());
    }
  }

  return result;
};
